import { Typography, Stack, Skeleton, Box, Chip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  LiveRecipeMaterial,
  useGetLiveRecipeQuery,
} from "src/store/api/generatedApi";
import ScienceIcon from "@mui/icons-material/Science";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useFormatNumber } from "src/utils/formatNumber";
import { OverviewCard } from "./OverviewCard";

const getDepletionColor = (
  heatsLeft: number | null
): "error" | "warning" | "success" | "default" => {
  if (heatsLeft === null) return "default";
  if (heatsLeft < 5) return "error";
  if (heatsLeft < 15) return "warning";
  return "success";
};

const HeatsLeftCell = ({
  value: heatsUntilDepletion,
}: GridRenderCellParams<LiveRecipeMaterial, number | null>) => (
  <Chip
    label={heatsUntilDepletion ?? "N/A"}
    size="small"
    color={getDepletionColor(heatsUntilDepletion ?? null)}
    sx={{ fontSize: 11, height: 20 }}
  />
);

export const LiveRecipe = () => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const formatNumber = useFormatNumber();

  const { data: liveRecipe, isLoading } = useGetLiveRecipeQuery({
    tenantName: tenant,
  });

  const columns: GridColDef<LiveRecipeMaterial>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      flex: 1,
    },
    {
      field: "percentage",
      headerName: "%",
      type: "number",
      width: 80,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value: number) => `${formatNumber(1)(value)}%`,
    },
    {
      field: "heats_until_depletion",
      headerName: t("heatsLeft"),
      type: "number",
      width: 100,
      align: "right",
      headerAlign: "right",
      display: "flex",
      renderCell: HeatsLeftCell,
    },
  ];

  return (
    <OverviewCard
      title={t("liveRecipe")}
      icon={ScienceIcon}
      buttonText={t("viewPlan")}
      buttonHref={`/${tenant}/deployed-plans`}
      delay={0.2}
    >
      {isLoading ? (
        <Skeleton variant="rectangular" height={250} />
      ) : liveRecipe ? (
        <Box sx={{ px: 2 }}>
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body2"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                {liveRecipe.mix_name}
              </Typography>
              <Chip
                label={t("upcomingHeats", {
                  count: liveRecipe.upcoming_heat_count,
                })}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Stack>

            <Box sx={{ maxHeight: 220, overflow: "auto" }}>
              <HotkeyCompliantDataGrid
                rows={liveRecipe.materials}
                columns={columns}
                getRowId={(material) => material.material_id}
                autoHeight
              />
            </Box>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ px: 2 }}>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("noLiveRecipe")}
          </Typography>
        </Box>
      )}
    </OverviewCard>
  );
};
